import { captureMessage } from '@sentry/react';
import type { AxiosError } from 'axios';
import { AxiosHeaders } from 'axios';
import { performLogout } from '@studio/storeV2/authentication/helpers/perform-logout';
import { session } from '@studio/storeV2/authentication/session-store';
import { axiosInstance } from '../axios-instance';
import { refreshAuthToken } from '../refresh-auth-token';

let isAlreadyFetchingAccessToken = false;
const pendingRetryApiQueue = new Map<string, (access_token: string) => void>();

function logoutOnRetryFailure(abortController: AbortController, originalError: AxiosError) {
  const url = originalError.config?.url ?? 'unknown';
  const status = originalError.response?.status ?? 'unknown';
  captureMessage(`Authentication failed after retry: ${url} (status: ${status})`, 'log');

  return new Promise((_res, rej) => {
    void performLogout({
      onFinally: () => {
        session.setTokenExpired();
        abortController.abort();
        pendingRetryApiQueue.clear();
        rej(new Error(`Authentication failed after retry: ${url}`, { cause: originalError }));
      },
    });
  });
}

async function resetTokenReattempt(error: AxiosError): Promise<unknown> {
  const pause = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
  const abortController = new AbortController();

  try {
    const { response: errorResponse } = error;
    const originalRequest = error.config;

    // @ts-expect-error adding a property to the request
    if (originalRequest._retry) {
      return await logoutOnRetryFailure(abortController, error);
    }

    const retryOriginalRequest = new Promise((resolve) => {
      if (errorResponse) {
        addPendingApiQueue(errorResponse.config.url ?? '', (access_token) => {
          errorResponse.config = {
            ...errorResponse.config,
            headers: AxiosHeaders.from({
              ...errorResponse.config.headers,
              Authorization: 'X-Bearer ' + access_token,
            }),
            signal: abortController.signal,
            // @ts-expect-error adding a property to the request
            _retry: true,
          };
          resolve(axiosInstance(errorResponse.config));
        });
      }
    });
    if (!isAlreadyFetchingAccessToken) {
      isAlreadyFetchingAccessToken = true;
      try {
        const newAuthToken = await refreshAuthToken();
        // Wait 2 seconds before retrying the call again
        await pause(2_000);
        onAccessTokenFetched(newAuthToken);
      } catch (fetchTokenError) {
        return Promise.reject(fetchTokenError);
      } finally {
        isAlreadyFetchingAccessToken = false;
      }
    }
    return retryOriginalRequest;
  } catch (err) {
    return Promise.reject(err);
  }
}

function onAccessTokenFetched(access_token: string) {
  Array.from(pendingRetryApiQueue.values()).forEach((callback) => {
    return callback(access_token);
  });

  pendingRetryApiQueue.clear();
}

function addPendingApiQueue(url: string, callback: (access_token: string) => void) {
  if (!url) return;
  if (!pendingRetryApiQueue.has(url)) {
    pendingRetryApiQueue.set(url, callback);
  }
}

export { resetTokenReattempt };
